import { companyNameOf, useCompanies } from '../companies.ts';
import { Eye, Pencil, Plus, RotateCw, Upload } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import {
  Badge,
  Button,
  PageHeader,
  formatNumber,
  useBreakpoint,
  type Column,
  type MenuAction,
  IconButton,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useMemo, useRef, useState } from 'react';
import { nameIn, type Category } from './categories.ts';
import { ActiveSwitch } from './list/ActiveSwitch.tsx';
import { BulkBar } from './list/BulkBar.tsx';
import { SELECTION_ONLY_ACTIONS, useProductActions, type StatusChange } from './list/product-actions.tsx';
import { withView, type ProductFilters } from './list/product-filters.ts';
import styles from './list/ProductList.module.css';
import { ProductKpis } from './list/ProductKpis.tsx';
import { ProductListTools, ProductViewChips } from './list/ProductToolbar.tsx';
import { QuickAddStockButton } from './list/QuickAddStockButton.tsx';
import { ShortcutsHelp } from './list/ShortcutsHelp.tsx';
import { StockCellView } from './list/StockCellView.tsx';
import { useCodeSearch } from './list/useCodeSearch.ts';
import { useProductList } from './list/useProductList.ts';
import { indexOptions, optionValueChoices } from './options.ts';
import { PriceTag } from './PriceTag.tsx';
import { ProductName } from './ProductName.tsx';
import { CATALOG_WRITE_ROLE, productStatusTone, type Product } from './product.ts';
import { namedVariantRows, productNameOf, variantRows } from './product-stock.ts';
import { ProductQuickView } from './ProductQuickView.tsx';
import { useActiveCategories, useOptions, useProduct, useTenantDetail } from './useCatalogQueries.ts';
import { ImportSheet } from '../imports/ImportSheet.tsx';
import { useProductStatusActions } from './useProductStatus.ts';
import { AdjustStockSheet } from '../inventory/AdjustStockSheet.tsx';
import { INVENTORY_WRITE_ROLE } from '../inventory/inventory.ts';
import { LoadMore } from '../LoadMore.tsx';
import { ResourceTable } from '../ResourceTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { useShortcuts } from '../useShortcuts.ts';
import { MAX_LABEL_PRODUCTS } from '../labels/labels.ts';

type Props = {
  api: TypedClient;
  filters: ProductFilters;
  openProductId: string | null;
  createdProductId: string | null;
  onFiltersChange: (filters: ProductFilters) => void;
  onOpenProduct: (productId: string | null) => void;
  onCreate: () => void;
  onEdit: (productId: string) => void;
  onPrintLabels: (productIds: readonly string[]) => void;
};

type Selection = { readonly scope: string; readonly ids: ReadonlySet<string> };

const categoryNameOf = (product: Product, names: ReadonlyMap<string, string>): string | null => {
  const [first] = product.categoryIds;
  return first === undefined ? null : (names.get(first) ?? null);
};

const neighbour = (products: readonly Product[], activeId: string | null, step: number): string | null => {
  const position = products.findIndex((product) => product.id === activeId);
  const next = products[Math.min(Math.max(position + step, 0), products.length - 1)];
  return next?.id ?? null;
};

export const ProductsPage = ({
  api,
  filters,
  openProductId,
  createdProductId,
  onFiltersChange,
  onOpenProduct,
  onCreate,
  onPrintLabels,
  onEdit,
}: Props) => {
  const { t, locale, warn } = usePageContext();
  const auth = useAuth();
  const canAdjustStock = auth.hasRole(INVENTORY_WRITE_ROLE);
  const isDesktop = useBreakpoint('md');
  const list = useProductList(api, filters);
  const categories = useActiveCategories(api);
  const opened = useProduct(api, openProductId);
  const options = useOptions(api);
  const statusActions = useProductStatusActions(api);
  const searchRef = useRef<HTMLInputElement>(null);
  const scope = JSON.stringify(filters);
  const [selection, setSelection] = useState<Selection>({ scope, ids: new Set() });
  const [activeId, setActiveId] = useState<string | null>(createdProductId);
  const [adjusting, setAdjusting] = useState<readonly Product[] | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const canImport = auth.hasRole(CATALOG_WRITE_ROLE);
  const tenant = useTenantDetail(api);
  const codeSearch = useCodeSearch({ api, filters, onFiltersChange, onOpenProduct });
  const [bulkBusy, setBulkBusy] = useState(false);

  const optionIndex = useMemo(() => indexOptions(options.data?.items ?? [], locale), [options.data, locale]);
  const categoryList: readonly Category[] = categories.data?.items ?? [];
  const companies = useCompanies(api);
  const categoryNames = new Map(
    categoryList.map((category) => [category.id, nameIn(category, locale, category.id)]),
  );
  const selectedIds = selection.scope === scope ? selection.ids : new Set<string>();
  const selectedProducts = list.products.filter((product) => selectedIds.has(product.id));
  const active = list.products.find((product) => product.id === activeId) ?? null;

  const select = (ids: ReadonlySet<string>) => {
    setSelection({ scope, ids });
  };

  const runStatus = (change: StatusChange, products: readonly Product[]) => {
    const acted = new Set(products.map((product) => product.id));
    setBulkBusy(true);
    void statusActions[change](products).finally(() => {
      setBulkBusy(false);
      select(new Set([...selectedIds].filter((id) => !acted.has(id))));
    });
  };

  const printLabelsOf = (products: readonly Product[]) => {
    if (products.length > MAX_LABEL_PRODUCTS)
      warn(t('labels.firstProducts', { max: formatNumber(MAX_LABEL_PRODUCTS, locale) }));
    onPrintLabels(products.map((product) => product.id));
  };

  const productActions = useProductActions({
    busy: bulkBusy,
    canAdjustStock,
    onStatus: runStatus,
    onAdjustStock: setAdjusting,
    onPrintLabels: printLabelsOf,
  });

  const rowActionsOf = (product: Product): readonly MenuAction[] => [
    {
      key: 'view',
      label: t('productList.details'),
      icon: <Eye size={16} aria-hidden="true" />,
      onSelect: () => {
        onOpenProduct(product.id);
      },
    },
    {
      key: 'edit',
      label: t('productList.edit'),
      icon: <Pencil size={16} aria-hidden="true" />,
      onSelect: () => {
        onEdit(product.id);
      },
    },
    ...productActions([product]).filter((action) => !SELECTION_ONLY_ACTIONS.includes(action.key)),
  ];

  useShortcuts({
    j: () => {
      setActiveId(neighbour(list.products, activeId, 1));
    },
    k: () => {
      setActiveId(neighbour(list.products, activeId, -1));
    },
    Enter: () => {
      if (active !== null) onOpenProduct(active.id);
    },
    e: () => {
      if (active !== null) onEdit(active.id);
    },
    a: () => {
      if (active !== null && canAdjustStock) setAdjusting([active]);
    },
    x: () => {
      if (active === null || !isDesktop) return;
      const next = new Set(selectedIds);
      if (next.has(active.id)) next.delete(active.id);
      else next.add(active.id);
      select(next);
    },
    '/': () => {
      searchRef.current?.focus();
    },
    '?': () => {
      setHelpOpen(true);
    },
    Escape: () => {
      select(new Set());
    },
  });

  const metaOf = (product: Product): string =>
    [
      t('productList.variantCount', { count: product.variants.length }),
      ...(product.subsidiaryId === null ? [] : [companyNameOf(companies, product.subsidiaryId)]),
      ...(product.createdBy === null ? [] : [t('productEditor.createdBy', { name: product.createdBy })]),
    ].join(' · ');

  const actionsColumn: Column<Product> = {
    id: 'actions',
    header: t('catalog.actions'),
    action: true,
    cell: (product) => (
      <span className={styles['rowActions']}>
        {rowActionsOf(product).map((action) => (
          <IconButton
            key={action.key}
            size="sm"
            label={t('common.rowAction', {
              action: action.label,
              name: nameIn(product, locale, product.id),
            })}
            icon={action.icon}
            {...(action.tone === undefined ? {} : { tone: action.tone })}
            disabled={action.disabled === true}
            onClick={action.onSelect}
          />
        ))}
      </span>
    ),
  };

  const columns: readonly Column<Product>[] = [
    {
      id: 'name',
      header: t('productList.product'),
      primary: true,
      cell: (product) => <ProductName product={product} locale={locale} meta={metaOf(product)} />,
    },
    {
      id: 'reference',
      header: t('productList.reference'),
      cell: (product) => <span className={styles['reference']}>{product.reference ?? '—'}</span>,
    },
    {
      id: 'category',
      header: t('catalog.category'),
      cell: (product) => product.categoryIds.map((id) => categoryNames.get(id) ?? id).join(', ') || '—',
    },
    {
      id: 'price',
      header: t('catalog.price'),
      numeric: true,
      cell: (product) => <PriceTag product={product} />,
    },
    {
      id: 'stock',
      header: t('productList.stock'),
      cell: (product) => (
        <StockCellView
          cell={list.stockOf(product)}
          addition={
            canAdjustStock && isDesktop ? (
              <QuickAddStockButton
                api={api}
                productName={productNameOf(product, locale)}
                rows={variantRows(product, optionIndex)}
              />
            ) : null
          }
        />
      ),
    },
    {
      id: 'active',
      header: t('productList.active'),
      interactive: true,
      cell: (product) => (
        <ActiveSwitch
          product={product}
          label={t('common.rowAction', {
            action: t('productList.active'),
            name: nameIn(product, locale, product.id),
          })}
          busy={bulkBusy}
          onStatus={runStatus}
        />
      ),
    },
    {
      id: 'status',
      header: t('catalog.status'),
      status: true,
      cell: (product) => (
        <Badge tone={productStatusTone[product.status]}>{t(`catalog.status_${product.status}`)}</Badge>
      ),
    },
    actionsColumn,
  ];

  const stockFailed = list.products.some((product) => list.stockOf(product) === 'failed');

  return (
    <>
      <PageHeader
        title={t('nav.products')}
        context={t('catalog.productsContext')}
        action={
          <span className={styles['headerActions']}>
            {canImport ? (
              <Button
                icon={<Upload size={20} aria-hidden="true" />}
                variant="outline"
                onClick={() => {
                  setImporting(true);
                }}
              >
                {t('productImport.openCsv')}
              </Button>
            ) : null}
            <Button icon={<Plus size={20} aria-hidden="true" />} onClick={onCreate}>
              {t('catalog.newProduct')}
            </Button>
          </span>
        }
      />
      <div className={styles['overview']}>
        <ProductKpis
          api={api}
          company={filters.company}
          optionValueId={filters.optionValueId}
          counts={list.counts}
          onShowView={(view) => {
            onFiltersChange(withView(filters, view));
          }}
        />
        <ProductViewChips filters={filters} counts={list.counts} onFiltersChange={onFiltersChange} />
      </div>
      {stockFailed ? (
        <p className={styles['notice']} role="status">
          {t('productList.stockUnavailable')}
          <Button
            icon={<RotateCw size={16} aria-hidden="true" />}
            variant="ghost"
            size="sm"
            onClick={list.retryStock}
          >
            {t('common.retry')}
          </Button>
        </p>
      ) : null}
      <ResourceTable
        query={list.query}
        columns={columns}
        rows={list.products}
        toolbar={
          <ProductListTools
            filters={filters}
            categories={categoryList}
            optionValues={optionValueChoices(options.data?.items ?? [], locale)}
            companies={companies}
            searchRef={searchRef}
            searchBoxKey={codeSearch.boxKey}
            onFiltersChange={onFiltersChange}
            onSearch={codeSearch.search}
            onSearchLeave={codeSearch.searchAsText}
            onShowShortcuts={() => {
              setHelpOpen(true);
            }}
          />
        }
        footer={
          list.products.length === 0 ? undefined : (
            <LoadMore
              shownLabel={t('productList.shown', {
                shown: formatNumber(list.products.length, locale),
                total: formatNumber(list.total, locale),
              })}
              hasMore={list.hasMore}
              loading={list.loadingMore}
              onLoadMore={list.loadMore}
            />
          )
        }
        rowKey={(product) => product.id}
        activeKey={activeId}
        highlightKey={createdProductId}
        selection={{
          selected: selectedIds,
          onChange: select,
          rowLabel: (product) =>
            t('productList.selectProduct', { name: nameIn(product, locale, product.id) }),
          allLabel: t('productList.selectAll'),
        }}
        onRowClick={(product) => {
          setActiveId(product.id);
          onOpenProduct(product.id);
        }}
        emptyTitle={t('catalog.noProducts')}
        emptyDescription={t('catalog.noProductsHelp')}
      />
      {selectedProducts.length === 0 || !isDesktop || openProductId !== null ? null : (
        <BulkBar
          count={selectedProducts.length}
          actions={productActions(selectedProducts)}
          onClear={() => {
            select(new Set());
          }}
        />
      )}
      {openProductId === null || opened.data === undefined ? null : (
        <ProductQuickView
          api={api}
          product={opened.data}
          categoryName={categoryNameOf(opened.data, categoryNames)}
          optionIndex={optionIndex}
          canAddStock={canAdjustStock}
          onClose={() => {
            onOpenProduct(null);
          }}
          onEdit={onEdit}
          onPrintLabels={(productId) => {
            onPrintLabels([productId]);
          }}
          onAdjustStock={
            canAdjustStock
              ? (product) => {
                  onOpenProduct(null);
                  setAdjusting([product]);
                }
              : null
          }
        />
      )}
      {adjusting === null ? null : (
        <AdjustStockSheet
          api={api}
          subtitle={
            adjusting.length === 1 && adjusting[0] !== undefined
              ? productNameOf(adjusting[0], locale)
              : t('adjustStock.products', { count: adjusting.length })
          }
          rows={
            adjusting.length === 1 && adjusting[0] !== undefined
              ? variantRows(adjusting[0], optionIndex)
              : namedVariantRows(adjusting, optionIndex, locale)
          }
          onClose={() => {
            setAdjusting(null);
          }}
        />
      )}
      {importing ? (
        <ImportSheet
          api={api}
          shopLocales={tenant.data?.locales ?? []}
          onClose={() => {
            setImporting(false);
          }}
        />
      ) : null}
      {helpOpen ? (
        <ShortcutsHelp
          onClose={() => {
            setHelpOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
